import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { checkPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const ReportsAnalytics: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();

  // State management
  const [activeTab, setActiveTab] = useState('Sales Reports');
  const [dateRange, setDateRange] = useState('This Month');
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState(false);
  const dateRangeDropdownRef = useRef<HTMLDivElement>(null);
  
  // Data state for dynamic loading
  const [salesData, setSalesData] = useState<any>(null);
  const [inventoryData, setInventoryData] = useState<any>(null);
  const [financialData, setFinancialData] = useState<any>(null);
  const [dailyBalancesData, setDailyBalancesData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // API functions for fetching dynamic data
  const fetchReportsData = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const params = new URLSearchParams({
        date_range: dateRange,
        ...(selectedBranchId && !isAllBranches ? { branch_id: selectedBranchId.toString() } : {})
      });

      // Fetch sales data
      const salesResponse = await fetch(`${getApiUrl(`reports/sales?${params.toString()}`)}`, { headers });
      if (!salesResponse.ok) {
        const errorData = await salesResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${salesResponse.status}: Failed to fetch sales data`);
      }
      const salesResult = await salesResponse.json();
      setSalesData(salesResult);

      // Fetch inventory data
      const inventoryResponse = await fetch(`${getApiUrl(`reports/inventory?${params.toString()}`)}`, { headers });
      if (!inventoryResponse.ok) {
        const errorData = await inventoryResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${inventoryResponse.status}: Failed to fetch inventory data`);
      }
      const inventoryResult = await inventoryResponse.json();
      setInventoryData(inventoryResult);

      // Fetch financial data
      const financialResponse = await fetch(`${getApiUrl(`reports/financial?${params.toString()}`)}`, { headers });
      if (!financialResponse.ok) {
        const errorData = await financialResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${financialResponse.status}: Failed to fetch financial data`);
      }
      const financialResult = await financialResponse.json();
      setFinancialData(financialResult);

      // Fetch daily balances data
      const dailyBalancesResponse = await fetch(`${getApiUrl(`reports/daily-balances?${params.toString()}`)}`, { headers });
      if (!dailyBalancesResponse.ok) {
        const errorData = await dailyBalancesResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${dailyBalancesResponse.status}: Failed to fetch daily balances data`);
      }
      const dailyBalancesResult = await dailyBalancesResponse.json();
      setDailyBalancesData(dailyBalancesResult);

    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to connect to server. Please check if the backend service is running.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load reports data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, dateRange, selectedBranchId, isAllBranches]);


  // Load data when component mounts or filters change
  useEffect(() => {
    fetchReportsData();
  }, [fetchReportsData]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target as Node)) {
        setIsDateRangeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check permissions
  if (!checkPermission(user?.role, 'reports')) {
    return <AccessDenied resource="Reports & Analytics" />;
  }

  // Date range options for reports
  const dateRangeOptions = [
    'This Month',
    'Last Month',
    'This Year',
    'Last Year'
  ];

  // Report tabs
  const reportTabs = [
    'Sales Reports',
    'Inventory Reports', 
    'Financial Reports',
    'Daily Balances'
  ];

  // Sample data for sales metrics
  const salesMetrics = {
    totalSalesToday: 125000,
    totalSalesThisMonth: 2850000,
    totalProfitToday: 35000,
    totalProfitThisMonth: 750000,
    averageSaleValue: 47500,
    numberOfTransactions: 86
  };

  // Sample data for inventory metrics
  const inventoryMetrics = {
    totalItems: 450,
    readyItems: 285,
    serviceItems: 45,
    reservedItems: 32,
    totalValue: 18500000
  };

  // Sample data for financial metrics
  const financialMetrics = {
    totalRevenue: 2850000,
    totalCosts: 2100000,
    grossProfit: 750000,
    profitMargin: 26.3
  };

  // Chart color scheme matching current dark blue primary theme (exact Tailwind values)
  const chartColors = {
    primary: '#334155',      // primary-500 (main theme color)
    secondary: '#94a3b8',    // primary-400 
    tertiary: '#cbd5e1',     // primary-300
    success: '#10b981',      // green (keep same)
    danger: '#ef4444',       // red (keep same)
    warning: '#f59e0b',      // yellow (keep same)
    info: '#3b82f6',         // blue (keep same)
    purple: '#8b5cf6',       // purple (keep same)
    gray: '#6b7280'          // gray (keep same)
  };

  // Chart options configuration
  const defaultChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  // Sales Trend Chart Data (Line Chart)
  const salesTrendChartData = {
    labels: ['Jan 1', 'Jan 2', 'Jan 3', 'Jan 4', 'Jan 5', 'Jan 6', 'Jan 7'],
    datasets: [
      {
        label: 'Sales',
        data: [45000, 52000, 38000, 65000, 48000, 72000, 58000],
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primary + '20',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Profit',
        data: [12000, 15000, 9500, 18500, 13200, 21000, 16800],
        borderColor: chartColors.success,
        backgroundColor: chartColors.success + '20',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  // Branch Performance Chart Data (Bar Chart)
  const branchPerformanceChartData = {
    labels: ['Main Branch', 'Branch 2', 'Branch 3', 'Branch 4', 'Branch 5'],
    datasets: [
      {
        label: 'Sales',
        data: [1250000, 850000, 680000, 520000, 380000],
        backgroundColor: chartColors.primary,
        borderRadius: 8,
      },
      {
        label: 'Profit',
        data: [325000, 220000, 165000, 125000, 85000],
        backgroundColor: chartColors.success,
        borderRadius: 8,
      }
    ]
  };

  // Top Selling Items Chart Data (Horizontal Bar)
  const topSellingItemsChartData = {
    labels: ['iPhone 14 Pro', 'Samsung Galaxy S23', 'OnePlus 11', 'Xiaomi 13 Pro', 'Google Pixel 7'],
    datasets: [
      {
        label: 'Revenue',
        data: [2200000, 1296000, 870000, 576000, 432000],
        backgroundColor: [
          chartColors.primary,
          chartColors.secondary,
          chartColors.info,
          chartColors.warning,
          chartColors.purple
        ],
        borderRadius: 8,
      }
    ]
  };

  // Inventory Status Chart Data (Doughnut Chart)
  const inventoryStatusChartData = {
    labels: ['Ready', 'Service', 'Reserved'],
    datasets: [
      {
        data: [285, 45, 32],
        backgroundColor: [
          chartColors.success,    // Green for Ready
          chartColors.warning,    // Yellow for Service
          chartColors.danger      // Red for Reserved (changed from info/blue)
        ],
        borderWidth: 0,
      }
    ]
  };

  // Inventory by Category Chart Data (Pie Chart)
  const inventoryByCategoryChartData = {
    labels: ['Mobile Phone', 'Laptop', 'Tablet', 'Accessories'],
    datasets: [
      {
        data: [280, 58, 19, 5],
        backgroundColor: [
          chartColors.primary,
          chartColors.info,
          chartColors.warning,
          chartColors.gray
        ],
        borderWidth: 0,
      }
    ]
  };

  // Financial Trends Chart Data (Area Chart)
  const financialTrendsChartData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Revenue',
        data: [650000, 720000, 680000, 800000],
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primary + '30',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Profit',
        data: [180000, 195000, 175000, 200000],
        borderColor: chartColors.success,
        backgroundColor: chartColors.success + '30',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Costs',
        data: [470000, 525000, 505000, 600000],
        borderColor: chartColors.danger,
        backgroundColor: chartColors.danger + '30',
        fill: true,
        tension: 0.4,
      }
    ]
  };


  // Icons
  const CalendarIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );


  const ChartIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const TrendingUpIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );

  const CurrencyIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
    </svg>
  );

  const PackageIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );

  // Render functions for different report sections
  const renderSalesReports = () => {
    // Show loading state
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Loading sales reports...</span>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading sales reports</h3>
              <div className="mt-1 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      );
    }

    // Use dynamic data if available, fallback to static data
    // Map API response structure to expected format
    const apiData = salesData?.data;
    const metrics = apiData?.summary ? {
      total_sales: apiData.summary.totalSales,
      total_profit: apiData.summary.totalProfit,
      total_transactions: apiData.summary.totalTransactions,
      average_sale_value: apiData.summary.averageSaleValue
    } : {
      total_sales: salesMetrics.totalSalesThisMonth,
      total_profit: salesMetrics.totalProfitThisMonth,
      total_transactions: salesMetrics.numberOfTransactions,
      average_sale_value: salesMetrics.averageSaleValue
    };
    
    const trends = apiData?.salesTrend || [];
    console.log('📊 [FRONTEND] Sales Reports Data:', {
      hasApiData: !!apiData,
      salesTrendLength: trends.length,
      salesTrendSample: trends.slice(0, 3),
      topProductsCount: apiData?.topProducts?.length || 0
    });
    
    const topItems = apiData?.topProducts?.map((product: any) => ({
      phone_model: product.name,
      total_revenue: product.revenue,
      quantity: product.quantity
    })) || [];
    
    // For now, use category breakdown as branch performance (until we add branch data to API)
    const branchPerformance = apiData?.categoryBreakdown ? 
      Object.entries(apiData.categoryBreakdown).map(([category, data]: [string, any]) => ({
        branch_name: category,
        total_sales: data.revenue,
        total_profit: data.profit
      })) : [];
    

    return (
      <div className="space-y-6">
        
        {/* Sales Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.total_sales || 0)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUpIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Profit</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.total_profit || 0)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CurrencyIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Sale Value</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.average_sale_value || 0)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <ChartIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.total_transactions || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <ChartIcon />
              </div>
            </div>
          </div>
        </div>

      {/* Sales Trend Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales & Profit Trends</h3>
        <div className="h-80">
          {trends.length > 0 ? (
            <Line 
              data={{
                labels: trends.map((t: any) => t.date || t.period),
                datasets: [
                  {
                    label: 'Sales',
                    data: trends.map((t: any) => t.sales || 0),
                    borderColor: chartColors.primary,
                    backgroundColor: chartColors.primary + '20',
                    fill: true,
                    tension: 0.4,
                  },
                  {
                    label: 'Profit',
                    data: trends.map((t: any) => t.profit || 0),
                    borderColor: chartColors.success,
                    backgroundColor: chartColors.success + '20',
                    fill: true,
                    tension: 0.4,
                  }
                ]
              }} 
              options={defaultChartOptions} 
            />
          ) : (
            <Line data={salesTrendChartData} options={defaultChartOptions} />
          )}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Items Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Selling Items by Revenue</h3>
          <div className="h-80">
            {topItems.length > 0 ? (
              <Bar 
                data={{
                  labels: topItems.map((item: any) => item.phone_model || item.name || 'Unknown'),
                  datasets: [{
                    label: 'Revenue',
                    data: topItems.map((item: any) => item.total_revenue || item.revenue || 0),
                    backgroundColor: [
                      chartColors.primary,
                      chartColors.secondary,
                      chartColors.info,
                      chartColors.warning,
                      chartColors.purple
                    ],
                    borderRadius: 8,
                  }]
                }}
                options={{
                  ...defaultChartOptions,
                  indexAxis: 'y' as const,
                  plugins: {
                    ...defaultChartOptions.plugins,
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value: any) {
                          return formatCurrency(value);
                        }
                      }
                    }
                  }
                }} 
              />
            ) : (
              <Bar 
                data={topSellingItemsChartData} 
                options={{
                  ...defaultChartOptions,
                  indexAxis: 'y' as const,
                  plugins: {
                    ...defaultChartOptions.plugins,
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value: any) {
                          return formatCurrency(value);
                        }
                      }
                    }
                  }
                }} 
              />
            )}
          </div>
        </div>

        {/* Category Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales by Category</h3>
          <div className="h-80">
            {branchPerformance.length > 0 ? (
              <Bar 
                data={{
                  labels: branchPerformance.map((branch: any) => branch.branch_name || 'Unknown'),
                  datasets: [
                    {
                      label: 'Sales',
                      data: branchPerformance.map((branch: any) => branch.total_sales || 0),
                      backgroundColor: chartColors.primary,
                      borderRadius: 8,
                    },
                    {
                      label: 'Profit',
                      data: branchPerformance.map((branch: any) => branch.total_profit || 0),
                      backgroundColor: chartColors.success,
                      borderRadius: 8,
                    }
                  ]
                }}
                options={defaultChartOptions} 
              />
            ) : (
              <Bar data={branchPerformanceChartData} options={defaultChartOptions} />
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderInventoryReports = () => {
    // Show loading state
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Loading inventory reports...</span>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading inventory reports</h3>
              <div className="mt-1 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      );
    }

    // Use dynamic data if available, fallback to static data
    // Map API response structure to expected format
    const apiData = inventoryData?.data;
    const metrics = apiData?.summary ? {
      total_items: apiData.summary.totalItems,
      ready_items: apiData.summary.readyItems,
      service_items: apiData.summary.serviceItems,
      reserved_items: apiData.summary.reservedItems,
      total_value: apiData.summary.totalValue,
      items_added: apiData.summary.totalItems, // Approximation
      items_sold: apiData.summary.soldItems,
      turnover_rate: apiData.summary.totalItems > 0 ? (apiData.summary.soldItems / apiData.summary.totalItems * 100) : 0
    } : {
      total_items: inventoryMetrics.totalItems,
      ready_items: inventoryMetrics.readyItems,
      service_items: inventoryMetrics.serviceItems,
      reserved_items: inventoryMetrics.reservedItems || 0,
      total_value: inventoryMetrics.totalValue,
      items_added: 45, // Static fallback
      items_sold: 32, // Static fallback
      turnover_rate: 8.5 // Static fallback
    };
    
    console.log('📊 [FRONTEND] Inventory Reports Data:', {
      hasApiData: !!apiData,
      statusDistribution: apiData?.statusDistribution,
      categoryBreakdown: apiData?.categoryBreakdown,
      summaryKeys: apiData?.summary ? Object.keys(apiData.summary) : 'no summary'
    });
    
    const statusDistribution = apiData?.statusDistribution ? 
      Object.entries(apiData.statusDistribution).map(([status, count]) => ({ status, count })) : [];
      
    const categoryDistribution = apiData?.categoryBreakdown ? 
      Object.entries(apiData.categoryBreakdown).map(([category, data]: [string, any]) => ({ 
        category, 
        count: data.quantity || 0  // Fixed: use 'quantity' instead of 'total'
      })) : [];
      

    return (
      <div className="space-y-6">
        {/* Inventory Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.total_items || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <PackageIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ready Items</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.ready_items || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <PackageIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Service</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.service_items || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <PackageIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.total_value || 0)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <CurrencyIcon />
              </div>
            </div>
          </div>
        </div>

      {/* Inventory Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Status Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Status Distribution</h3>
          <div className="h-80">
            {statusDistribution.length > 0 ? (
              <Doughnut 
                data={{
                  labels: statusDistribution.map((item: any) => {
                    if (item.status === 'ready') return 'Ready';
                    if (item.status === 'service') return 'Service';
                    if (item.status === 'reserved') return 'Reserved';
                    if (item.status === 'sold') return 'Sold';
                    return item.status;
                  }),
                  datasets: [{
                    data: statusDistribution.map((item: any) => item.count || 0),
                    backgroundColor: statusDistribution.map((item: any) => {
                      if (item.status === 'ready') return chartColors.success;      // Green for Ready
                      if (item.status === 'service') return chartColors.warning;   // Yellow for Service  
                      if (item.status === 'reserved') return chartColors.danger;   // Red for Reserved
                      if (item.status === 'sold') return chartColors.info;         // Blue for Sold
                      return chartColors.gray; // Fallback color
                    }),
                    borderWidth: 0,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                          size: 12
                        }
                      }
                    }
                  }
                }} 
              />
            ) : (
              <Doughnut 
                data={inventoryStatusChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                          size: 12
                        }
                      }
                    }
                  }
                }} 
              />
            )}
          </div>
        </div>

        {/* Inventory by Category */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Distribution by Category</h3>
          <div className="h-80">
            {categoryDistribution.length > 0 ? (
              <Pie 
                data={{
                  labels: categoryDistribution.map((item: any) => {
                    const category = item.category;
                    if (category === 'mobile_phone') return 'Mobile Phone';
                    if (category === 'laptop') return 'Laptop';
                    if (category === 'tablet') return 'Tablet';
                    if (category === 'accessories') return 'Accessories';
                    return category;
                  }),
                  datasets: [{
                    data: categoryDistribution.map((item: any) => item.count || 0),
                    backgroundColor: [
                      chartColors.primary,
                      chartColors.info,
                      chartColors.warning,
                      chartColors.gray
                    ],
                    borderWidth: 0,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                          size: 12
                        }
                      }
                    }
                  }
                }} 
              />
            ) : (
              <Pie 
                data={inventoryByCategoryChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                          size: 12
                        }
                      }
                    }
                  }
                }} 
              />
            )}
          </div>
        </div>
      </div>

      {/* Stock Movement Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock Movement Analysis</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{metrics.items_added || 0}</p>
            <p className="text-sm text-blue-600">Items Added</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{metrics.items_sold || 0}</p>
            <p className="text-sm text-green-600">Items Sold</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{metrics.service_items || 0}</p>
            <p className="text-sm text-yellow-600">In Service Queue</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{(metrics.turnover_rate || 0).toFixed(1)}%</p>
            <p className="text-sm text-purple-600">Turnover Rate</p>
          </div>
        </div>

      </div>
    </div>
    );
  };

  const renderFinancialReports = () => {
    // Show loading state
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Loading financial reports...</span>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading financial reports</h3>
              <div className="mt-1 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      );
    }

    // Use dynamic data if available, fallback to static data
    // Map API response structure to expected format
    const apiData = financialData?.data;
    console.log('📊 [FRONTEND] Financial Reports Data:', {
      hasApiData: !!apiData,
      summaryKeys: apiData?.summary ? Object.keys(apiData.summary) : 'no summary',
      monthlyRevenueKeys: apiData?.monthlyRevenue ? Object.keys(apiData.monthlyRevenue) : 'no monthlyRevenue',
      paymentMethodKeys: apiData?.paymentMethodBreakdown ? Object.keys(apiData.paymentMethodBreakdown) : 'no paymentMethods'
    });
    
    const metrics = apiData?.summary ? {
      total_revenue: apiData.summary.totalRevenue,
      total_costs: apiData.summary.totalCosts,
      gross_profit: apiData.summary.totalProfit,  // Fixed: use totalProfit instead of grossProfit
      profit_margin: apiData.summary.profitMargin
    } : {
      total_revenue: financialMetrics.totalRevenue,
      total_costs: financialMetrics.totalCosts,
      gross_profit: financialMetrics.grossProfit,
      profit_margin: financialMetrics.profitMargin
    };
    
    // Fixed: Use monthlyRevenue instead of revenueTrend
    const trends = apiData?.monthlyRevenue ? 
      Object.entries(apiData.monthlyRevenue).map(([month, data]: [string, any]) => ({
        month,
        revenue: data.revenue || 0,
        profit: data.profit || 0,
        costs: (data.revenue || 0) - (data.profit || 0)  // Calculate costs
      })) : [];
    
    // Debug logging for trends data
    console.log('📊 [FRONTEND] Financial Trends Data:', {
      hasApiData: !!apiData,
      hasMonthlyRevenue: !!apiData?.monthlyRevenue,
      trendsLength: trends.length,
      trendsData: trends,
      sampleTrend: trends[0] || 'no trends'
    });
      
    const expenseBreakdown = apiData?.expenseCategoryBreakdown ? 
      Object.entries(apiData.expenseCategoryBreakdown).map(([category, data]: [string, any]) => ({
        expense_type: category.toLowerCase().replace(' ', '_'),
        total_amount: data.amount || 0,
        count: data.count || 0
      })) : [];
      
    const paymentMethodBreakdown = apiData?.paymentMethodBreakdown ? 
      Object.entries(apiData.paymentMethodBreakdown).map(([method, data]: [string, any]) => ({
        payment_method: method,
        total_amount: data.revenue,  // Fixed: use revenue instead of amount
        count: data.count
      })) : [];

    return (
      <div className="space-y-6">
        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.total_revenue || 0)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUpIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Costs</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.total_costs || 0)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <CurrencyIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Gross Profit</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.gross_profit || 0)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CurrencyIcon />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Profit Margin</p>
                <p className="text-2xl font-bold text-gray-900">{(metrics.profit_margin || 0).toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <ChartIcon />
              </div>
            </div>
          </div>
        </div>

      {/* Financial Trends Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Trends (Revenue, Profit, Costs)</h3>
        <div className="h-96">
          {trends.length > 0 ? (
            <Line 
              data={{
                labels: trends.map((t: any) => t.month || t.period),
                datasets: [
                  {
                    label: 'Revenue',
                    data: trends.map((t: any) => t.revenue || 0),
                    borderColor: chartColors.primary,
                    backgroundColor: chartColors.primary + '30',
                    fill: true,
                    tension: 0.4,
                  },
                  {
                    label: 'Profit',
                    data: trends.map((t: any) => t.profit || 0),
                    borderColor: chartColors.success,
                    backgroundColor: chartColors.success + '30',
                    fill: true,
                    tension: 0.4,
                  },
                  {
                    label: 'Costs',
                    data: trends.map((t: any) => t.costs || 0),
                    borderColor: chartColors.danger,
                    backgroundColor: chartColors.danger + '30',
                    fill: true,
                    tension: 0.4,
                  }
                ]
              }}
              options={defaultChartOptions} 
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-lg font-medium mb-2">No Financial Trends Data</p>
                <p className="text-sm text-gray-400 mb-4">No sales data available for the selected period</p>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>• Financial trends are based on sales records</p>
                  <p>• Try selecting a different date range</p>
                  <p>• Ensure sales data exists for the period</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expense Breakdown and Payment Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Breakdown</h3>
          <div className="space-y-4">
            {expenseBreakdown.length > 0 ? (
              expenseBreakdown.map((expense: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${
                      expense.expense_type === 'office supplies' ? 'bg-red-500' :
                      expense.expense_type === 'utilities' ? 'bg-yellow-500' :
                      expense.expense_type === 'maintenance' ? 'bg-blue-500' :
                      expense.expense_type === 'marketing' ? 'bg-green-500' :
                      'bg-gray-500'
                    }`}></div>
                    <span className="text-sm text-gray-600 capitalize">
                      {expense.expense_type.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="font-medium">{formatCurrency(expense.total_amount || 0)}</span>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-500 mb-2">No expense records found</p>
                  <p className="text-xs text-gray-400">for the selected date range</p>
                </div>
              </div>
            )}
            <hr className="my-2" />
            <div className="flex items-center justify-between font-semibold">
              <span>Total Expenses</span>
              <span>{formatCurrency(expenseBreakdown.reduce((total, expense) => total + (expense.total_amount || 0), 0))}</span>
            </div>
          </div>
        </div>

        {/* Payment Method Analysis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method Analysis</h3>
          <div className="space-y-6">
            {paymentMethodBreakdown.length > 0 ? (
              paymentMethodBreakdown.map((payment: any, index: number) => {
                const percentage = metrics.total_revenue > 0 ? (payment.total_amount / metrics.total_revenue * 100) : 0;
                return (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600 capitalize">
                        {payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 
                         payment.payment_method === 'upi' ? 'UPI' :
                         payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1)}
                      </span>
                      <span className="text-sm font-medium">{formatCurrency(payment.total_amount || 0)} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          index === 0 ? 'bg-green-600' :
                          index === 1 ? 'bg-blue-600' :
                          index === 2 ? 'bg-yellow-600' :
                          'bg-purple-600'
                        }`}
                        style={{width: `${percentage}%`}}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Cash</span>
                    <span className="text-sm font-medium">60.2%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{width: '60.2%'}}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">UPI</span>
                    <span className="text-sm font-medium">22.3%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{width: '22.3%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Card</span>
                    <span className="text-sm font-medium">12.8%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{width: '12.8%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Bank Transfer</span>
                    <span className="text-sm font-medium">4.7%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{width: '4.7%'}}></div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Daily Balances Report Component
  const renderDailyBalances = () => {
    const balancesData = dailyBalancesData?.data;
    console.log('📊 [FRONTEND] Daily Balances Data:', {
      hasBalancesData: !!balancesData,
      summaryKeys: balancesData?.summary ? Object.keys(balancesData.summary) : 'no summary',
      dailyRecordsLength: balancesData?.dailyRecords?.length || 0,
      chartDataExists: !!balancesData?.chartData,
      sampleDailyRecord: balancesData?.dailyRecords?.[0] || 'no data'
    });
    
    // formatCurrency is now imported from '../utils/currency'
    
    // Use chart data from backend (matching old implementation)
    const chartData = balancesData?.chartData;

    return (
      <div className="space-y-6">

        {/* Summary Cards - Matching Original Layout */}
        {balancesData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Current Balance */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Current Balance</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(balancesData.summary.currentBalance || 0)}</p>
                  <p className="text-xs text-gray-500">{(balancesData.summary.netChangePercentage || 0).toFixed(1)}% change</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Sales */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Sales</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency((balancesData.summary.totalSalesCash || 0) + (balancesData.summary.totalSalesDigital || 0))}</p>
                  <p className="text-xs text-gray-500">Cash: {formatCurrency(balancesData.summary.totalSalesCash || 0)} | Digital: {formatCurrency(balancesData.summary.totalSalesDigital || 0)}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Expenses */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(balancesData.summary.totalExpenses || 0)}</p>
                  <p className="text-xs text-gray-500">Avg Daily: {formatCurrency(balancesData.summary.avgDailyExpenses || 0)}</p>
                </div>
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Net Change */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Net Change</p>
                  <p className={`text-2xl font-bold ${(balancesData.summary.netChange || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(balancesData.summary.netChange || 0) >= 0 ? '+' : ''}{formatCurrency(balancesData.summary.netChange || 0)}
                  </p>
                  <p className="text-xs text-gray-500">Range: {formatCurrency(balancesData.summary.lowestBalance || 0)} - {formatCurrency(balancesData.summary.highestBalance || 0)}</p>
                </div>
                <div className={`p-2 rounded-lg ${(balancesData.summary.netChange || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <svg className={`w-6 h-6 ${(balancesData.summary.netChange || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Balance Trend Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Daily Balance Trends - {dateRange}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Track daily balance, sales, and expenses over time
            </div>
          </div>
          
          {isLoading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : chartData ? (
            <div className="h-96">
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top' as const,
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: { size: 12 }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      callbacks: {
                        label: function(context: any) {
                          return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value: any) {
                          return formatCurrency(value);
                        }
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  }
                }}
              />
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-lg font-medium mb-2">No Daily Balance Data</p>
                <p className="text-sm text-gray-400 mb-4">No daily balance records found for the selected period</p>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>• Daily balances are recorded when cash transactions occur</p>
                  <p>• Try selecting a different date range</p>
                  <p>• Contact your administrator to set up daily balance tracking</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Daily Records Table */}
        {balancesData?.dailyRecords && balancesData.dailyRecords.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Daily Balance Records</h3>
              <p className="text-sm text-gray-500 mt-1">Daily balance information for the selected period</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Sales</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Digital Sales</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Withdrawals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deposits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {balancesData.dailyRecords.slice(0, 10).map((record: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(record.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(record.balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(record.salesCash)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(record.salesDigital)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(record.expenses)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(record.withdrawals)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(record.deposits)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {record.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {balancesData.dailyRecords.length > 10 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                Showing first 10 records of {balancesData.dailyRecords.length} total records
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Daily Balance Records</h3>
              <p className="text-sm text-gray-500 mt-1">Daily balance information for the selected period</p>
            </div>
            <div className="p-8 text-center">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 mb-2">No daily balance records found</p>
              <p className="text-sm text-gray-400">Daily balance records will appear here when transactions are recorded</p>
            </div>
          </div>
        )}
      </div>
    );
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'Sales Reports':
        return renderSalesReports();
      case 'Inventory Reports':
        return renderInventoryReports();
      case 'Financial Reports':
        return renderFinancialReports();
      case 'Daily Balances':
        return renderDailyBalances();
      default:
        return renderSalesReports();
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-500 mt-1">
            Comprehensive business insights and performance analytics
            {!isAllBranches && selectedBranchId ? (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                📍 Filtering: {selectedBranchName}
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                🌐 Showing: All Branches
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          {/* Date Range Selector */}
          <div className="relative" ref={dateRangeDropdownRef}>
            <button 
              onClick={() => setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
            >
              <CalendarIcon />
              <span className="text-sm font-medium text-gray-700">{dateRange}</span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDateRangeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isDateRangeDropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {dateRangeOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setDateRange(option);
                      setIsDateRangeDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      dateRange === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Branch Filter Info */}
      {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Branch Filter Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Showing only reports data from <strong>{selectedBranchName}</strong>. 
                Select "All Branches" to see analytics from all locations.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Category Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {reportTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics;